import type { NeoSource } from "./neoSources";

// Matches ``[[source:<id>]]`` where the id is the restricted charset the
// backend allow-list permits (alphanumerics, ``: . _ -``). Anchoring to that
// class keeps the post-render HTML substitution injection-safe.
export const SOURCE_TOKEN_RE = /\[\[source:([a-zA-Z0-9_:.-]+)\]\]/g;

// Matches a *run* of one-or-more adjacent ``[[source:…]]`` markers separated
// only by whitespace. The model emits multiple citations for the same claim
// back-to-back (``…overnight [[source:note:pcc:1]][[source:alert:2]]``); we
// render the run as one numbered chip per distinct source, so the whitespace
// between markers is absorbed and the chips' own margins space them evenly.
// Whitespace is allowed *between* markers but not trailing, so a needed space
// after the run stays.
export const SOURCE_RUN_RE =
  /\[\[source:[a-zA-Z0-9_:.-]+\]\](?:\s*\[\[source:[a-zA-Z0-9_:.-]+\]\])*/g;

// Neo wraps patient names in ``<[patientId]>Name<[]>`` id tags in its
// interactive-chat replies (so the name can eventually be linkified). Until
// that linkification exists, strip the raw markers client-side so the user
// never sees them — this removes the opening ``<[123]>`` and closing ``<[]>``
// forms alike, leaving the bare name. Matches any ``<[...]>`` with no ``]``
// inside, which is safe: the tags never contain a ``]`` and ordinary prose
// effectively never produces this exact shape.
const PATIENT_ID_TAG_RE = /<\[[^\]]*\]>/g;
export const stripPatientIdTags = (text: string): string =>
  text.replace(PATIENT_ID_TAG_RE, "");

// Linkified form (2026 UI). Each complete ``<[p-<clinicPatientId>]>Name<[]>``
// pair is lifted out BEFORE markdown rendering: the text keeps only an opaque
// private-use placeholder (``\uE000<index>\uE001``) and the id + name go into
// a side list. ``renderPatientLinks`` then swaps each rendered placeholder for
// a button whose label is the name HTML-escaped as plain text, so markdown in a
// tagged name (``<[p-5]>[Bob](https://x)<[]>``) can never become a link or
// other markup. Any other or unpaired ``<[...]>`` is stripped as before.
const PATIENT_LINK_OPEN = "\uE000";
const PATIENT_LINK_CLOSE = "\uE001";
const PATIENT_ID_PAIR_RE = /<\[p-(\d+)\]>([^<\n]{1,120}?)<\[\]>/g;
const RENDERED_PATIENT_LINK_RE = new RegExp(
  `${PATIENT_LINK_OPEN}(\\d+)${PATIENT_LINK_CLOSE}`,
  "g"
);
const STRAY_PATIENT_LINK_RE = new RegExp(
  `[${PATIENT_LINK_OPEN}${PATIENT_LINK_CLOSE}]`,
  "g"
);

export interface PatientLinkTag {
  /** ``clinic_patient.id`` (digits only). */
  id: string;
  /** Raw name text as the model wrote it; escaped when rendered. */
  name: string;
}

export const markPatientIdTags = (
  text: string
): { text: string; links: PatientLinkTag[] } => {
  const links: PatientLinkTag[] = [];
  const marked = text.replace(
    PATIENT_ID_PAIR_RE,
    (_match, id: string, name: string) => {
      links.push({ id, name: name.trim() });
      return `${PATIENT_LINK_OPEN}${links.length - 1}${PATIENT_LINK_CLOSE}`;
    }
  );
  return { text: stripPatientIdTags(marked), links };
};

/** Swap ``markPatientIdTags`` placeholders in rendered HTML for name buttons
 *  carrying ``data-neo-patient``. The label is escaped plain text and the id
 *  is digits only; unknown or stray placeholders are dropped. */
export const renderPatientLinks = (
  html: string,
  links: PatientLinkTag[],
  className = ""
): string =>
  html
    .replace(RENDERED_PATIENT_LINK_RE, (_match, index: string) => {
      const link = links[Number(index)];
      if (!link) return "";
      return `<button type="button" class="${escapeHtmlAttr(
        className
      )}" data-neo-patient="${link.id}">${escapeHtmlAttr(link.name)}</button>`;
    })
    .replace(STRAY_PATIENT_LINK_RE, "");

const escapeHtmlAttr = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

/** Move every ``[[source:...]]`` marker on a line to the *end* of that line,
 *  collected together. The model tends to drop a marker mid-clause — often
 *  right before the sentence's closing punctuation (``"…desatted [[…]]."``) —
 *  which renders a chip awkwardly wedged inside the sentence. Relocating the
 *  markers to the line end means the chip always sits after the text and its
 *  final punctuation, and adjacent markers there render as a tidy run of
 *  numbered chips via ``processHtmlSources``.
 *
 *  Done on the raw markdown (pre-render) so whitespace left behind is trivial
 *  to tidy. Leading indentation is preserved (so nested list items keep their
 *  level) and table rows (any line containing ``|``) are left untouched — a
 *  table cell's citation must stay in its own cell, not jump to the row end. */
export const moveSourceMarkersToLineEnd = (src: string): string =>
  src
    .split("\n")
    .map((line) => {
      // Leave table rows alone — moving a marker would yank it out of its
      // cell into the last column.
      if (line.includes("|")) return line;
      const lead = (line.match(/^[ \t]*/) ?? [""])[0];
      const markers: string[] = [];
      let body = line.slice(lead.length).replace(SOURCE_TOKEN_RE, (m) => {
        markers.push(m);
        return "";
      });
      if (!markers.length) return line;
      // Tidy the gaps the removed markers left: a space before punctuation,
      // a doubled space mid-line, and any trailing run. (Leading indentation
      // is outside ``body`` so it's never collapsed.)
      body = body
        .replace(/[ \t]+([.,;:!?])/g, "$1")
        .replace(/[ \t]{2,}/g, " ")
        .replace(/[ \t]+$/g, "");
      return `${lead}${body}${markers.join("")}`;
    })
    .join("\n");

/** Replace runs of ``[[source:...]]`` tokens in already-rendered HTML with
 *  inline grey circled-numeral badges — one per cited source — that the user
 *  can click to open the citation drawer for that source.
 *
 *  Numbering runs per message and is assigned on first appearance, the way
 *  footnote references work: the reader meets ``(1) (2) (3)`` in order, and a
 *  source cited again later re-uses the number it was given the first time.
 *
 *  We operate on the *rendered* HTML (not the raw markdown) because
 *  markdown-it runs with ``html: false`` — injecting ``<button>`` into the
 *  source text would just get escaped and shown literally. Post-render
 *  substitution stays safe: the token charset is restricted and every
 *  data/title attribute is HTML-escaped.
 *
 *  ``chipClassName`` is the host surface's own badge class (each surface
 *  styles its chips to match its typography); the click target is always
 *  the ``data-neo-sources`` attribute. */
export const processHtmlSources = (
  html: string,
  resolve: (sid: string) => NeoSource | undefined,
  medRecVisible: boolean,
  chipClassName: string
): string => {
  const numberBySource = new Map<string, number>();
  return html.replace(SOURCE_RUN_RE, (run: string) => {
    // Collect the resolvable ids in this run, in order, de-duplicated.
    const seen = new Set<string>();
    const ids = (run.match(SOURCE_TOKEN_RE) ?? [])
      .map((tok) => tok.slice("[[source:".length, -"]]".length))
      .filter((sid) => {
        if (seen.has(sid)) return false;
        const src = resolve(sid);
        const type = src?.source_type ?? sid.split(":")[0];
        // Discharge med-rec citations only navigate to the (beta-gated) Med
        // Rec tab; when it's unavailable the chip would lead nowhere, so drop
        // the marker rather than render a dead link.
        if (type === "discharge_medrec" && !medRecVisible) return false;
        // A marker the cache can't resolve has no drawer/navigation target
        // (the backend verifier guarantees only data-backed markers survive,
        // so an unresolved one just wasn't in this turn's payload).
        if (!src) return false;
        seen.add(sid);
        return true;
      });
    if (!ids.length) return "";

    // Grey circled numeral — matches Compassus Navigator Notes ``.ref-icon``.
    return ids
      .map((sid) => {
        let num = numberBySource.get(sid);
        if (num === undefined) {
          num = numberBySource.size + 1;
          numberBySource.set(sid, num);
        }
        const ariaLabel = `Source ${num}`;
        // Hovering a chip should say *which* record it points at, not just
        // repeat the number the reader can already see.
        const tooltip = resolve(sid)?.title || ariaLabel;
        // Lets the stylesheet shrink the numeral to fit the fixed-width
        // circle. Capped at the largest bucket the stylesheet defines.
        const digits = Math.min(String(num).length, 4);
        return `<button class="${escapeHtmlAttr(
          chipClassName
        )}" data-neo-sources="${escapeHtmlAttr(
          sid
        )}" data-neo-digits="${digits}" type="button" title="${escapeHtmlAttr(
          tooltip
        )}" aria-label="${escapeHtmlAttr(ariaLabel)}">${num}</button>`;
      })
      .join("");
  });
};

/** Pull the statement a citation chip backs out of the rendered message.
 *
 *  The chip carries no text of its own, so we read the block it lives in
 *  (paragraph / list item / table cell / heading) and return the sentence
 *  immediately *preceding* the marker — that's the clause the source is cited
 *  as evidence for. Falls back to the whole block when the chip sits at the
 *  start. Returns ``undefined`` if nothing usable is found. */
export const extractCitationClaim = (btn: Element): string | undefined => {
  const block =
    btn.closest("p,li,td,th,h1,h2,h3,h4,h5,h6,blockquote") ?? btn.parentElement;
  if (!block) return undefined;

  // Reads text out of a *detached copy* (it mutates what it is given) with
  // the chips dropped: a chip's own numeral is not part of the sentence, and
  // a claim backed by several sources has sibling chips right before this one.
  const textWithoutChips = (copy: Element | DocumentFragment): string => {
    copy
      .querySelectorAll("[data-neo-sources]")
      .forEach((chip) => chip.remove());
    return (copy.textContent ?? "").replace(/\s+/g, " ").trim();
  };

  let before = "";
  try {
    const range = document.createRange();
    range.selectNodeContents(block);
    range.setEndBefore(btn);
    before = textWithoutChips(range.cloneContents());
  } catch {
    before = textWithoutChips(block.cloneNode(true) as Element);
  }

  const whole = textWithoutChips(block.cloneNode(true) as Element);
  if (!before) return whole || undefined;

  // Last sentence of the text leading up to the marker.
  const sentences = before.split(/(?<=[.!?:])\s+/);
  const claim = sentences[sentences.length - 1]?.trim();
  return claim || before || whole || undefined;
};
